import { useEffect, useRef, useState } from "react";

import { useApiCall } from "@framework/react-hooks";

import {
  TRAIN_ROTATION_DIRECTION,
  type ITrainDetailsRotationResponse,
} from "../../scheme/components/analytics/interfaces";
import { ROTATION_DIRECTION } from "../types";

export const mapTrainRotationToToolbar = (
  value: TRAIN_ROTATION_DIRECTION | null | undefined,
): ROTATION_DIRECTION | null => {
  switch (value) {
    case TRAIN_ROTATION_DIRECTION.COUNTER_CLOCKWISE:
      return ROTATION_DIRECTION.COUNTER_CLOCKWISE;
    case TRAIN_ROTATION_DIRECTION.CLOCKWISE:
      return ROTATION_DIRECTION.CLOCKWISE;
    default:
      return null;
  }
};

interface IUseTrainRotationDirectionArgs {
  enabled: boolean;
  trainId: number | null;
}

export const useTrainRotationDirection = ({ enabled, trainId }: IUseTrainRotationDirectionArgs) => {
  const [trainRotationDirection, setTrainRotationDirection] = useState<TRAIN_ROTATION_DIRECTION>(
    TRAIN_ROTATION_DIRECTION.UNDEFINED,
  );
  const [isLoading, setIsLoading] = useState(false);
  const loadedTrainIdRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  const { fn: fetchRotationDirection } = useApiCall(
    (api, { idTrain }: { idTrain: number }): Promise<TRAIN_ROTATION_DIRECTION> => {
      return (
        api.fetchJson({
          url: `/trains/${idTrain}/details`,
          method: "GET",
        }) as Promise<ITrainDetailsRotationResponse[]>
      ).then(json => json[0]?.RotationDirection ?? TRAIN_ROTATION_DIRECTION.UNDEFINED);
    },
    { success: false },
  );

  const fetchRotationDirectionRef = useRef(fetchRotationDirection);

  useEffect(() => {
    fetchRotationDirectionRef.current = fetchRotationDirection;
  }, [fetchRotationDirection]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || trainId === null) {
      loadedTrainIdRef.current = null;
      setTrainRotationDirection(TRAIN_ROTATION_DIRECTION.UNDEFINED);
      setIsLoading(false);
      return;
    }

    if (loadedTrainIdRef.current === trainId) {
      return;
    }

    setIsLoading(true);

    void fetchRotationDirectionRef
      .current(undefined, { idTrain: trainId })
      .then(direction => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        loadedTrainIdRef.current = trainId;
        setTrainRotationDirection(direction ?? TRAIN_ROTATION_DIRECTION.UNDEFINED);
      })
      .catch(error => {
        console.error(error);

        if (requestIdRef.current === requestId) {
          loadedTrainIdRef.current = null;
          setTrainRotationDirection(TRAIN_ROTATION_DIRECTION.UNDEFINED);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [enabled, trainId]);

  const lockedToolbarDirection = mapTrainRotationToToolbar(trainRotationDirection);

  return {
    trainRotationDirection,
    lockedToolbarDirection,
    isRotationDirectionLocked: lockedToolbarDirection !== null,
    isLoading,
  };
};
